import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

/** Generic fallback for AppLayout Suspense (shown during lazy chunk loading) */
export function GenericPageSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-150">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="glass-panel">
            <CardHeader className="pb-2"><Skeleton className="h-4 w-24" /></CardHeader>
            <CardContent><Skeleton className="h-8 w-16" /></CardContent>
          </Card>
        ))}
      </div>
      <Card className="glass-panel">
        <CardContent className="pt-6 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

/** Skeleton for the Landings list page */
export function LandingsPageSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-9 w-36 rounded-md" />
      </div>
      <Card className="glass-panel hidden md:block">
        <CardContent className="p-0 pt-4 space-y-0">
          {/* Table header */}
          <div className="flex gap-4 px-4 pb-3 border-b border-border">
            {["w-32", "w-20", "w-16", "w-12", "w-24", "w-16", "w-24"].map((w, i) => (
              <Skeleton key={i} className={`h-4 ${w}`} />
            ))}
          </div>
          {/* Table rows */}
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-4 items-center px-4 py-3 border-b border-border last:border-0">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-8" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <div className="flex gap-1 ml-auto">
                {Array.from({ length: 4 }).map((_, j) => (
                  <Skeleton key={j} className="h-8 w-8 rounded-md" />
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} className="glass-panel">
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between">
                <div className="space-y-1.5">
                  <Skeleton className="h-5 w-36" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <div className="flex gap-2">
                <Skeleton className="h-5 w-14 rounded-full" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/** Skeleton for the Leads page */
export function LeadsPageSkeleton() {
  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <Skeleton className="h-9 w-64 rounded-md" />
        <Skeleton className="h-9 w-32 rounded-md" />
        <Skeleton className="h-9 w-32 rounded-md" />
        <Skeleton className="h-9 w-24 rounded-md ml-auto" />
      </div>
      {/* Table */}
      <Card className="glass-panel">
        <CardContent className="p-0 pt-4 space-y-0">
          <div className="flex gap-4 px-4 pb-3 border-b border-border">
            {["w-40", "w-24", "w-32", "w-20", "w-24"].map((w, i) => (
              <Skeleton key={i} className={`h-4 ${w}`} />
            ))}
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex gap-4 items-center px-4 py-3 border-b border-border last:border-0">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

/** Skeleton for Settings page */
export function SettingsPageSkeleton() {
  return (
    <div className="space-y-6 max-w-2xl">
      <Skeleton className="h-8 w-36" />
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} className="glass-panel">
          <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-4 w-full max-w-sm" />
            <Skeleton className="h-9 w-full rounded-md" />
            <Skeleton className="h-9 w-28 rounded-md" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/** Skeleton for Brand page */
export function BrandPageSkeleton() {
  return (
    <div className="space-y-6 max-w-2xl">
      <div className="space-y-1">
        <Skeleton className="h-8 w-36" />
        <Skeleton className="h-4 w-72" />
      </div>
      {/* Logo card */}
      <Card className="glass-panel">
        <CardHeader><Skeleton className="h-5 w-16" /></CardHeader>
        <CardContent><Skeleton className="h-32 w-full rounded-lg" /></CardContent>
      </Card>
      {/* Color card */}
      <Card className="glass-panel">
        <CardHeader><Skeleton className="h-5 w-36" /></CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <Skeleton className="h-9 w-12 rounded" />
            <Skeleton className="h-9 flex-1 rounded-md" />
          </div>
        </CardContent>
      </Card>
      {/* Fonts card */}
      <Card className="glass-panel">
        <CardHeader><Skeleton className="h-5 w-20" /></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-9 w-full rounded-md" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-9 w-full rounded-md" />
          </div>
        </CardContent>
      </Card>
      <Skeleton className="h-9 w-32 rounded-md" />
    </div>
  );
}

/** Skeleton for Link in Bio page */
export function LinkInBioPageSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-32" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Editor side */}
        <div className="space-y-6">
          {/* Profile section */}
          <Card className="glass-panel">
            <CardHeader><Skeleton className="h-5 w-20" /></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-9 w-full rounded-md" />
                  <Skeleton className="h-16 w-full rounded-md" />
                </div>
              </div>
              <Skeleton className="h-9 w-28 rounded-md" />
            </CardContent>
          </Card>
          {/* Design section */}
          <Card className="glass-panel">
            <CardHeader><Skeleton className="h-5 w-16" /></CardHeader>
            <CardContent className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-9 flex-1 rounded-md" />
                </div>
              ))}
            </CardContent>
          </Card>
          {/* Blocks section */}
          <Card className="glass-panel">
            <CardHeader><Skeleton className="h-5 w-16" /></CardHeader>
            <CardContent className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
              <Skeleton className="h-9 w-full rounded-md" />
            </CardContent>
          </Card>
        </div>
        {/* Preview side */}
        <div className="space-y-3">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-[500px] w-full max-w-sm rounded-2xl mx-auto" />
        </div>
      </div>
    </div>
  );
}

/** Skeleton for Integrations page */
export function IntegrationsPageSkeleton() {
  return (
    <div className="space-y-4 max-w-2xl">
      <Skeleton className="h-8 w-36" />
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} className="glass-panel">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-5 w-44" />
              </div>
              <Skeleton className="h-5 w-5 rounded" />
            </div>
          </CardHeader>
        </Card>
      ))}
    </div>
  );
}
